import { useInfiniteQuery, UseInfiniteQueryOptions, useQuery } from '@tanstack/react-query';
import { connectionsApi } from '../api';
import * as _ from 'es-toolkit';

// Query keys
export const queryKeys = {
  connections: ['connections'] as const,
  userConnections: (userId?: number) => ['connections', 'user', userId] as const,
  userFollows: (userId: number) => ['connections', 'user', userId, 'follows'] as const,
} as const;

export interface ConnectionsQuery {
  fromType?: string;
  fromId?: number;
  toType?: string;
  toId?: number;
  toIds?: number[];
  tags?: string[];
  systemTags?: string[];
  rolesOnly?: boolean;
  roles?: string[];
  relationshipsOnly?: boolean;
  relationships?: string[];
  limit?: number;
  nextCursor?: string;
  prevCursor?: string;
}
export interface ApiConnection {
  id: number;
  fromId: number;
  fromType: string;
  toId: number;
  toType: string;
  tags: string[];
  systemTags: string[];
  data: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  // Enriched data
  entity?: {
    id: number;
    type: string;
    name_1?: string;
    name_2?: string;
    image?: string;
    status: string;
  };
}

export interface ConnectionsResponse {
  connections: ApiConnection[];
  pagination: {
    limit: number;
    nextCursor: string | null;
    prevCursor: string | null;
    hasMore: boolean;
  };
}

// Hook to fetch connections with optional filtering and pagination
export function useConnections(
  query?: ConnectionsQuery,
  options?: Partial<UseInfiniteQueryOptions<ConnectionsResponse>>
) {
  return useInfiniteQuery({
    queryKey: [...queryKeys.connections, query],
    queryFn: async (x) => {
      const response: ConnectionsResponse = await connectionsApi.getConnections(
        _.merge({ nextCursor: x.pageParam }, query || {})
      );
      return response;
    },
    staleTime: 1000 * 10,
    getNextPageParam: (lastPage) => lastPage.pagination.nextCursor,
    initialPageParam: '',
    // @ts-ignore
    ...(options as any),
  });
}

// Hook to fetch user's connections to entities with resource roles
export function useUserEntityConnections(
  userId?: number,
  options?: Partial<UseInfiniteQueryOptions<ConnectionsResponse>> & { rolesOnly?: boolean }
) {
  const { rolesOnly, ...queryOptions } = options || {};

  return useConnections(
    {
      fromType: 'user',
      fromId: userId,
      toType: 'entity',
      rolesOnly: rolesOnly ?? true, // Default to true for backward compatibility
    },
    {
      enabled: !!userId,
      ...queryOptions,
    }
  );
}

/**
 * Hook to fetch user's follows (entities and items)
 * Returns a map of objectType:objectId -> connection for quick lookup
 */
export function useUserFollows(userId?: number) {
  return useQuery({
    queryKey: queryKeys.userFollows(userId!),
    queryFn: async () => {
      const response = await connectionsApi.getConnections({
        fromType: 'user',
        fromId: userId,
        tags: ['follow'],
        limit: 100, // Get all follows
      });

      // Create a map for quick lookup: "entity:123" -> connection
      const followsMap = new Map<string, ApiConnection>();
      response.connections.forEach((conn) => {
        const key = `${conn.toType}:${conn.toId}`;
        followsMap.set(key, conn);
      });

      return followsMap;
    },
    enabled: !!userId,
    staleTime: 1000 * 10,
  });
}

/**
 * Helper hook to check if user is following a specific object
 */
export function useIsFollowing(objectId: number, objectType: 'entity' | 'item', userId?: number) {
  const { data: followsMap } = useUserFollows(userId);
  const key = `${objectType}:${objectId}`;
  return followsMap?.has(key) ?? false;
}
